/**
 * TODO List View
 *
 * A single, simple task list for the authenticated user — no projects. Tasks
 * are loaded flat (todoApi.listTasks with no project) and grouped client-side
 * into three status tabs (Completed, In Progress, Backlog) with `In Progress`
 * selected by default. New tasks are created without a projectId, so the
 * backend files them under the owner's default "General" project transparently.
 *
 * Each task card shows the title, description, priority and deadline, with
 * inline status change, edit and delete.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import todoApi from '../services/todoApi';
import StatusTab from './StatusTab';
import TodoTaskForm from './TodoTaskForm';
import './TodoList.css';

// The three statuses, in display order. `In Progress` is the default tab.
export const STATUSES = ['In Progress', 'Backlog', 'Completed'];
export const DEFAULT_STATUS = 'In Progress';

const STATUS_OPTIONS = STATUSES;

/**
 * Group a flat list of tasks into buckets keyed by status. Every known status
 * gets a bucket (possibly empty) so tab counts and empty states are consistent.
 */
export function groupByStatus(tasks) {
  const groups = {};
  STATUSES.forEach((status) => {
    groups[status] = [];
  });
  (tasks || []).forEach((task) => {
    if (task && Object.prototype.hasOwnProperty.call(groups, task.status)) {
      groups[task.status].push(task);
    }
  });
  return groups;
}

/**
 * Format an ISO calendar date (YYYY-MM-DD) for display. Returns null when the
 * value is missing or unparseable so the caller can omit the field entirely.
 */
function formatDeadline(deadline) {
  if (!deadline || typeof deadline !== 'string') {
    return null;
  }
  const parsed = new Date(`${deadline}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Return true when a deadline (YYYY-MM-DD) is strictly before today, used to
 * flag overdue tasks that are not yet completed.
 */
function isOverdue(deadline, status) {
  if (!deadline || status === 'Completed') {
    return false;
  }
  const parsed = new Date(`${deadline}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return false;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsed < today;
}

const TaskCard = ({ task, onDelete, onChangeStatus, onEdit, busy }) => {
  const deadlineLabel = formatDeadline(task.deadline);
  const overdue = isOverdue(task.deadline, task.status);
  const hasPriority = typeof task.priority === 'string' && task.priority.length > 0;
  const hasDescription =
    typeof task.description === 'string' && task.description.trim().length > 0;

  return (
    <div
      className={`todo-card todo-card--${(task.status || '')
        .toLowerCase()
        .replace(/\s+/g, '-')}`}
    >
      <div className="todo-card__main">
        <h4 className="todo-card__title">{task.title}</h4>

        {hasDescription && (
          <p className="todo-card__description">{task.description}</p>
        )}

        <div className="todo-card__meta">
          {hasPriority && (
            <span
              className={`todo-card__priority todo-card__priority--${task.priority.toLowerCase()}`}
            >
              {task.priority}
            </span>
          )}
          {deadlineLabel && (
            <span
              className={`todo-card__deadline ${
                overdue ? 'todo-card__deadline--overdue' : ''
              }`.trim()}
            >
              <span aria-hidden="true">📅</span> {deadlineLabel}
              {overdue && <span className="todo-card__overdue-tag">Overdue</span>}
            </span>
          )}
        </div>
      </div>

      <div className="todo-card__actions">
        <label className="todo-card__status-label">
          <span className="visually-hidden">Change status</span>
          <select
            className="todo-card__status-select"
            value={task.status}
            disabled={busy}
            onChange={(e) => onChangeStatus(task, e.target.value)}
          >
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="todo-card__btn todo-card__btn--edit"
          disabled={busy}
          onClick={() => onEdit && onEdit(task)}
        >
          Edit
        </button>
        <button
          type="button"
          className="todo-card__btn todo-card__btn--delete"
          disabled={busy}
          onClick={() => onDelete(task)}
        >
          Delete
        </button>
      </div>
    </div>
  );
};

const TodoList = () => {
  const [tasks, setTasks] = useState([]);
  const [activeStatus, setActiveStatus] = useState(DEFAULT_STATUS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyIds, setBusyIds] = useState([]);
  // Create/edit form state: null when closed, else { mode, task }.
  const [form, setForm] = useState(null);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // No projectId -> the backend returns ALL of the user's tasks.
      const data = await todoApi.listTasks();
      setTasks(Array.isArray(data?.tasks) ? data.tasks : []);
    } catch (e) {
      setError('Could not load your tasks. Please try again.');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const grouped = useMemo(() => groupByStatus(tasks), [tasks]);
  const visibleTasks = grouped[activeStatus] || [];

  const markBusy = (taskId, isBusy) => {
    setBusyIds((prev) =>
      isBusy ? [...prev, taskId] : prev.filter((id) => id !== taskId)
    );
  };

  const handleChangeStatus = async (task, nextStatus) => {
    if (!task || nextStatus === task.status) return;
    setError('');
    markBusy(task.taskId, true);
    try {
      await todoApi.changeStatus(task.taskId, nextStatus);
      setTasks((prev) =>
        prev.map((t) =>
          t.taskId === task.taskId ? { ...t, status: nextStatus } : t
        )
      );
    } catch (e) {
      setError('Could not update the task status. Please try again.');
    } finally {
      markBusy(task.taskId, false);
    }
  };

  const handleDelete = async (task) => {
    if (!task) return;
    const ok = window.confirm(`Delete "${task.title}"? This cannot be undone.`);
    if (!ok) return;
    setError('');
    markBusy(task.taskId, true);
    try {
      await todoApi.deleteTask(task.taskId);
      setTasks((prev) => prev.filter((t) => t.taskId !== task.taskId));
    } catch (e) {
      setError('Could not delete the task. Please try again.');
    } finally {
      markBusy(task.taskId, false);
    }
  };

  const handleOpenCreate = () => {
    setError('');
    setForm({ mode: 'create', task: null });
  };

  const handleOpenEdit = (task) => {
    if (!task) return;
    setError('');
    setForm({ mode: 'edit', task });
  };

  const handleCloseForm = () => setForm(null);

  // Submit handler passed to the form. New tasks are created without a
  // projectId (backend files them under the default project).
  const handleSubmitForm = async (payload) => {
    if (form?.mode === 'edit' && form.task) {
      await todoApi.editTask(form.task.taskId, payload);
    } else {
      await todoApi.createTask(payload);
    }
    await loadTasks();
  };

  const totalCount = tasks.length;

  return (
    <div className="todo-list">
      <div className="todo-list__header">
        <div className="todo-list__heading-group">
          <h2 className="todo-list__heading">My TODO List</h2>
          <p className="todo-list__subtitle">
            {totalCount === 0
              ? 'No tasks yet — add your first one.'
              : `${totalCount} task${totalCount === 1 ? '' : 's'} total`}
          </p>
        </div>
        <button
          type="button"
          className="todo-list__create-btn"
          onClick={handleOpenCreate}
        >
          <span aria-hidden="true">＋</span> New Task
        </button>
      </div>

      <div className="todo-list__tabs" role="tablist" aria-label="Task status">
        {STATUSES.map((status) => (
          <StatusTab
            key={status}
            status={status}
            count={(grouped[status] || []).length}
            active={status === activeStatus}
            onSelect={setActiveStatus}
          />
        ))}
      </div>

      {error && (
        <div className="todo-list__error" role="alert">
          {error}
        </div>
      )}

      <div className="todo-list__body" role="tabpanel">
        {loading ? (
          <div className="todo-list__loading">Loading your tasks…</div>
        ) : visibleTasks.length === 0 ? (
          <div className="todo-list__empty">
            {totalCount === 0 ? (
              <>
                <div className="todo-list__empty-icon" aria-hidden="true">📝</div>
                <p className="todo-list__empty-title">Your list is empty</p>
                <p className="todo-list__empty-hint">
                  Click <strong>New Task</strong> to add your first to-do.
                </p>
              </>
            ) : (
              <>
                <div className="todo-list__empty-icon" aria-hidden="true">✅</div>
                <p className="todo-list__empty-title">
                  Nothing in {activeStatus}
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="todo-list__cards">
            {visibleTasks.map((task) => (
              <TaskCard
                key={task.taskId}
                task={task}
                busy={busyIds.includes(task.taskId)}
                onDelete={handleDelete}
                onChangeStatus={handleChangeStatus}
                onEdit={handleOpenEdit}
              />
            ))}
          </div>
        )}
      </div>

      {form && (
        <TodoTaskForm
          mode={form.mode}
          initialTask={form.task}
          onSubmit={handleSubmitForm}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
};

export default TodoList;
